import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Scan from "@/pages/Scan";
import ScanAction from "@/pages/ScanAction";
import ScanDryAction from "@/pages/ScanDryAction";
import ItemDetail from "@/pages/ItemDetail";
import Items from "@/pages/Items";
import MealItems from "@/pages/MealItems";
import MealItemDetail from "@/pages/MealItemDetail";
import Reorder from "@/pages/Reorder";
import Sessions from "@/pages/Sessions";
import SessionDetail from "@/pages/SessionDetail";
import MealCalculator from "@/pages/MealCalculator";
import InventoryTransactions from "@/pages/InventoryTransactions";
import Admin from "@/pages/Admin";
import ImportCenter from "@/pages/admin/ImportCenter";
import MealCodes from "@/pages/admin/MealCodes";
import Locations from "@/pages/admin/Locations";
import Users from "@/pages/admin/Users";
import AuditLogs from "@/pages/admin/AuditLogs";
import QRPrint from "@/pages/admin/QRPrint";
import AddMeal from "@/pages/admin/AddMeal";
import AddDryGoods from "@/pages/admin/AddDryGoods";
import ChangePassword from "@/pages/ChangePassword";
import NotificationPreferences from "@/pages/NotificationPreferences";
import NotificationHistory from "@/pages/NotificationHistory";
import NotificationSettings from "@/pages/admin/NotificationSettings";
import "@/App.css";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="font-mono text-xs tracking-widest uppercase">Loading…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// iter19: If the user has `must_change_password` set, force them to the change
// password page and block navigation elsewhere until they complete the change.
function MustChangePasswordGuard({ children }) {
  const { user } = useAuth();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (user && user !== false && user.must_change_password && !path.startsWith("/change-password")) {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: { borderRadius: "4px", fontFamily: "IBM Plex Sans, sans-serif" },
          }}
        />
        <Routes>
          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MustChangePasswordGuard>
                  <Layout />
                </MustChangePasswordGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="scan" element={<Scan />} />
            <Route path="scan/action/:mealId" element={<ScanAction />} />
            <Route path="scan/dry-action/:variantId" element={<ScanDryAction />} />
            <Route path="items" element={<Items />} />
            <Route path="items/:variantId" element={<ItemDetail />} />
            <Route path="meal-items" element={<MealItems />} />
            <Route path="meal-items/:mealId" element={<MealItemDetail />} />
            <Route path="reorder" element={<Reorder />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="sessions/:sessionId" element={<SessionDetail />} />
            <Route path="meal-calculator" element={<MealCalculator />} />
            <Route path="transactions" element={<InventoryTransactions />} />
            <Route path="notifications" element={<NotificationPreferences />} />
            <Route path="notifications/history" element={<NotificationHistory />} />
            <Route path="account/change-password" element={<ChangePassword />} />
            <Route
              path="admin"
              element={
                <ProtectedRoute roles={["admin", "system_owner"]}>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route path="admin/import" element={<ProtectedRoute roles={["admin", "system_owner"]}><ImportCenter /></ProtectedRoute>} />
            <Route path="admin/meal-codes" element={<ProtectedRoute roles={["admin", "supervisor", "system_owner"]}><MealCodes /></ProtectedRoute>} />
            <Route path="admin/locations" element={<ProtectedRoute roles={["admin", "supervisor", "system_owner"]}><Locations /></ProtectedRoute>} />
            <Route path="admin/users" element={<ProtectedRoute roles={["admin", "system_owner"]}><Users /></ProtectedRoute>} />
            <Route path="admin/audit" element={<ProtectedRoute roles={["admin", "system_owner"]}><AuditLogs /></ProtectedRoute>} />
            <Route path="admin/qr-print" element={<ProtectedRoute roles={["admin", "supervisor", "system_owner"]}><QRPrint /></ProtectedRoute>} />
            <Route path="admin/add-meal" element={<ProtectedRoute roles={["admin", "system_owner"]}><AddMeal /></ProtectedRoute>} />
            <Route path="admin/add-dry-goods" element={<ProtectedRoute roles={["admin", "system_owner"]}><AddDryGoods /></ProtectedRoute>} />
            <Route path="admin/notifications" element={<ProtectedRoute roles={["admin", "system_owner"]}><NotificationSettings /></ProtectedRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
